/**
 * Withdrawal as extraction (IDEAS X22; decision 27's L2 verb "attempt to withdraw"). One mechanic for
 * getting out alive: a side that withdraws has to reach its **exit** — where it landed, the lane it came
 * by — and leave under fire. A unit that stands inside its team's exit region for `EXTRACT_SECONDS`
 * is **extracted**: off the field (dead to every rule that asks `alive`), but a survivor to the sector,
 * which books it as what came back. The battle model already takes a withdraw input with pursuit
 * losses; this is the full battle's shape of the same verb. Pure.
 */
import type { GameState, PlayerState, Vec3 } from '../types';
import { vecAdd, vecLength, vecNormalize, vecScale } from '../math';
import { PHYS_DT } from '../constants';
import { dcos } from './dmath';
import { squadOf, squadTaskFor } from './squads';

/** A team's way out: a cap of surface directions on one planet. */
export interface ExitRegion { planetIndex: number; facing: Vec3; halfAngle: number; /** 69.3a: the relay this region drops at */ relayId?: number }

/** Standing inside the exit this long gets a unit out (the ferry's pickup, abstracted). */
export const EXTRACT_SECONDS = 3;
/** The exit's half-angle when it is drawn from where a team landed (radians). */
export const EXIT_HALF_ANGLE = 0.25;

/**
 * Each team's exit, drawn from where it stands at the start: the mean direction of its units on the
 * planet most of them stand on (or the team's deploy region's facing where the setup gave one).
 */
export function exitsFromStart(players: readonly PlayerState[], facings: ReadonlyArray<Vec3 | undefined> = []): Array<ExitRegion | undefined> {
  const teams = [...new Set(players.map(p => p.teamIndex))].sort((a, b) => a - b);
  const out: Array<ExitRegion | undefined> = [];
  for (const t of teams) {
    const mine = players.filter(p => p.teamIndex === t);
    if (!mine.length) continue;
    const counts = new Map<number, number>();
    for (const p of mine) counts.set(p.planetIndex, (counts.get(p.planetIndex) ?? 0) + 1);
    const planetIndex = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0][0];
    const on = mine.filter(p => p.planetIndex === planetIndex);
    let facing = facings[t];
    if (!facing) {
      let sum: Vec3 = { x: 0, y: 0, z: 0 };
      for (const p of on) sum = vecAdd(sum, vecNormalize(p.surfaceDir));
      facing = vecLength(sum) > 1e-9 ? vecNormalize(sum) : vecNormalize(on[0].surfaceDir);
    }
    out[t] = { planetIndex, facing: vecNormalize(facing), halfAngle: EXIT_HALF_ANGLE };
  }
  return out;
}

/** Whether a surface direction on `planetIndex` is inside the exit. */
export function inExit(exit: ExitRegion, planetIndex: number, surfaceDir: Vec3): boolean {
  if (planetIndex !== exit.planetIndex) return false;
  const d = vecNormalize(surfaceDir);
  const dot = d.x * exit.facing.x + d.y * exit.facing.y + d.z * exit.facing.z;
  return dot >= dcos(exit.halfAngle) - 1e-9;
}

/** Is this unit's squad under a withdraw task right now? */
export function withdrawing(state: Pick<GameState, 'squads'>, unitId: number, tick: number): boolean {
  return squadTaskFor(state, unitId, tick)?.order.kind === 'withdraw';
}

/**
 * One tick of extraction: every living unit whose squad is withdrawing and that stands in its team's exit
 * accrues time there; at `EXTRACT_SECONDS` it is extracted (alive false, `extracted` true, logged in
 * `extractions`). Leaving the exit resets the clock. No-op without exits.
 */
export function stepExtraction(state: GameState, tick: number): GameState {
  const exits = state.exits;
  if (!exits || !state.squads?.some(sq => sq.tasks.some(t => t.order.kind === 'withdraw'))) return state;
  const need = Math.round(EXTRACT_SECONDS / PHYS_DT);
  const progress: Record<number, number> = { ...(state.extracting ?? {}) };
  const out = [...(state.extractions ?? [])];
  let players = state.players;
  let changed = false;
  for (const p of state.players) {
    if (!p.alive) { if (progress[p.id] !== undefined) { delete progress[p.id]; changed = true; } continue; }
    const exit = exits[p.teamIndex];
    const sq = squadOf(state, p.id);
    if (!exit || !sq || !withdrawing(state, p.id, tick) || !inExit(exit, p.planetIndex, p.surfaceDir)) {
      if (progress[p.id] !== undefined) { delete progress[p.id]; changed = true; }
      continue;
    }
    const since = progress[p.id] ?? tick;
    if (progress[p.id] === undefined) { progress[p.id] = tick; changed = true; }
    if (tick - since >= need) {
      players = players.map(q => (q.id === p.id ? { ...q, alive: false, extracted: true } : q));
      out.push({ unitId: p.id, teamIndex: p.teamIndex, tick });
      delete progress[p.id];
      changed = true;
    }
  }
  if (!changed) return state;
  return { ...state, players, extracting: progress, extractions: out };
}

/** The exit's centre as a surface direction, for a bot heading out. */
export function exitDir(exit: ExitRegion): Vec3 { return vecScale(vecNormalize(exit.facing), 1); }
